import chroma from 'chroma-js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { ReactComponent as BrazilMapSvg } from '../../assets/br.svg'
import { DataChart } from '../ChartWrapper'
import {
  getRegionsMapFormattedId,
  getTransformedMapData
} from '../../utils/regionsMapUtils'
import { Title } from '../styles'
import { BrazilMapProps, LegendData } from './BrazilMap.types'
import * as utils from './utils'
import { StyledBrazilChart } from './BrazilMap.styles'
import MapLegend from './MapLegend'

function BrazilMap({
  chartModel,
  data,
  options,
  title,
  format = 'none',
  chartCustomization,
  mapLegend
}: BrazilMapProps) {
  const svgRef = useRef<SVGSVGElement>(null)

  const { t } = useTranslation()

  const mapRenderingRef = useRef({ stateMap: false, regionMap: false })

  const { displayMode, defaultColor, colorAxis } = options || {}

  const isRegionMap = displayMode === 'regions'
  const isStateMap = displayMode === 'states'

  const { language: locale } = useTranslation()

  const transformedData: DataChart = useMemo(() => {
    return getTransformedMapData(data)
  }, [data])

  const coloredData = useMemo(() => {
    const values = transformedData.slice(1).map((item) => Number(item[1]))
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    const colorScale = chroma
      .scale(colorAxis.colors)
      .domain([minValue, maxValue])
    return transformedData.slice(1).map(([region, value]) => ({
      region,
      value,
      color: value ? colorScale(Number(value)).hex() : defaultColor
    }))
  }, [colorAxis.colors, transformedData, defaultColor])

  const updateRegionChart = useCallback(() => {
    if (!isRegionMap) return

    utils.applyColors({
      coloredData,
      defaultColor,
      isRegionMap,
      isStateMap,
      svgElement: svgRef
    })

    utils.setLabels({
      labelSelector: '#regions_label',
      data: transformedData,
      svgElement: svgRef,
      coloredData,
      format,
      locale,
      idTransformer: (id: string) =>
        getRegionsMapFormattedId({
          id,
          defaultTranslatedLabels: {
            centroOeste: t('maps.regions.centroOeste'),
            nordeste: t('maps.regions.nordeste'),
            norte: t('maps.regions.norte'),
            sudeste: t('maps.regions.sudeste'),
            sul: t('maps.regions.sul')
          },
          chartCustomization
        })
    })

    mapRenderingRef.current.regionMap = true
  }, [
    isRegionMap,
    coloredData,
    defaultColor,
    isStateMap,
    transformedData,
    format,
    locale,
    chartCustomization,
    t
  ])

  const updateStateChart = useCallback(() => {
    if (!isStateMap) return

    utils.applyColors({
      coloredData,
      defaultColor,
      isRegionMap,
      isStateMap,
      svgElement: svgRef
    })

    utils.setLabels({
      labelSelector: '#states_label',
      data: transformedData,
      svgElement: svgRef,
      coloredData,
      format,
      locale,
      idTransformer: (id: string) => id.replace('BR-', '')
    })

    mapRenderingRef.current.stateMap = true
  }, [
    isStateMap,
    coloredData,
    defaultColor,
    isRegionMap,
    transformedData,
    format,
    locale
  ])

  useEffect(() => {
    if (isRegionMap && !mapRenderingRef.current.regionMap) {
      updateRegionChart()
    } else if (isStateMap && !mapRenderingRef.current.stateMap) {
      updateStateChart()
    }
  }, [isRegionMap, isStateMap, updateRegionChart, updateStateChart])

  const sortedLegendData: LegendData[] = useMemo(() => {
    return coloredData
      .filter((item) => item.value !== null && item.value !== undefined)
      .map(({ region, value, ...rest }) => ({
        value: Number(value),
        ...rest
      }))
      .sort((a, b) => a.value - b.value)
  }, [coloredData])

  return (
    <StyledBrazilChart {...{ chartModel }}>
      {title && <Title>{title}</Title>}
      <BrazilMapSvg width={'100%'} ref={svgRef} />
      {mapLegend?.title && (
        <MapLegend
          title={mapLegend.title}
          data={sortedLegendData}
          format={format}
        />
      )}
    </StyledBrazilChart>
  )
}

export default BrazilMap
